/**
 * Mark-wind reading quality — which mark readings the Mark Wind graphic is allowed to believe.
 *
 * A mark whose wind sensor has failed reports a near-zero TWS with a meaningless TWD, and nothing upstream
 * rejects it: the live path only grades DRIFTING marks (mark BSP > 2 km/h, see server_stream/controllers/
 * liveMarkWind.js) and the batch `markwind` object carries no grade at all. So a dead sensor gets drawn as a
 * real arrow AND interpolated into the flow field, where it drags the field down around that mark, skews the
 * mean flow that advects everything, and pulls the legend's lower bound to zero.
 *
 * The rule here is deliberately RELATIVE: a sub-threshold reading is only ignored while another mark is
 * currently measuring real wind. When the whole course reads low that is a genuine calm and every reading
 * stands — which is why this cannot be a plain clamp.
 *
 * Pure and dependency-free (hence `utils/`, not the fetch service): mirrors `boatWindSamples.ts`, which is the
 * same thing for the boat half of the same wind field. Speeds are km/h everywhere in this pipeline — Influx
 * `TWS_MDSS_km_h_1`, Parquet `Tws_kph`, `WindSample.tws`, the "TWS km/h" legend — so the threshold needs no
 * unit conversion.
 */

/**
 * Below this (km/h) a mark's wind is treated as a dead reading — but ONLY while another mark in the same
 * timestep is measuring at or above it. 2 km/h is barely over 1 knot: no usable course wind sits under it.
 */
export const MARK_WIND_DEAD_TWS_KPH = 2;

/**
 * The mark-wind payload, structurally. Four separate declarations of it already exist (markwindService's
 * `MarkwindTimestep`, WindGridOverlay's local shadow of the same name, RaceCourseLayer's `MarkData`, and
 * windSampleHistory's `MarkwindTimestepLike`), so the functions below are generic over the caller's own type
 * rather than forcing a conversion at any call site. `any[]` rather than `Array<Record<string, any>>` on
 * purpose: an interface gets no implicit index signature, so the stricter element type would reject a
 * typechecked caller passing `MarkwindMark[]`.
 */
export interface MarkWindTimestepLike {
  MARKS?: any[] | null;
}

/**
 * TWS/TWD key spellings, in the priority order the renderers read them in.
 *
 * Only `TWS`/`TWD` are actually emitted today (by 5_markwind.py and liveMarkWind.js). The lowercase and
 * capitalised variants mirror the defensive fallback chains already in RaceCourseLayer's two renderers — they
 * are not evidence that some producer spells it differently, and the `marks` project object carries no wind at
 * all. Matching all three costs nothing and keeps this rule from being the one place that misses a spelling.
 */
const TWS_KEYS = ["tws", "TWS", "Tws"] as const;
const TWD_KEYS = ["twd", "TWD", "Twd"] as const;

/** First present TWS spelling parsed as a number, or NaN when the mark carries no numeric wind speed. */
function readTws(mark: Record<string, any>): number {
  for (const key of TWS_KEYS) {
    const raw = mark[key];
    if (raw === undefined || raw === null || raw === "") continue;
    const n = typeof raw === "number" ? raw : parseFloat(String(raw));
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

/**
 * One timestep in, a new timestep out with dead readings' wind stripped.
 *
 * Never mutates: the batch object is cached in `unifiedDataStore` and shared between both map consumers, and
 * the live payload is served from `markwindService`'s TTL cache to both at once. Idempotent, because
 * RaceCourseLayer re-renders the already-filtered `currentMarks` on every map move.
 *
 * An ignored mark keeps its NAME and position — it is still a mark, only its wind is untrustworthy — and gains
 * `WIND_IGNORED: true`. The flag is load-bearing, not decoration: RaceCourseLayer's `persistentMarks` map
 * deliberately preserves a mark's LAST known wind whenever the incoming reading is missing, so stripping TWS
 * on its own would leave the stale arrow frozen on screen.
 */
export function ignoreDeadMarkWind<T extends MarkWindTimestepLike>(timestep: T | null | undefined): T | null {
  if (!timestep) return null;
  const marks = timestep.MARKS;
  if (!Array.isArray(marks) || marks.length === 0) return timestep;

  const speeds = marks.map((m) => (m && typeof m === "object" ? readTws(m) : NaN));

  // The relative guard. Only a mark MEASURING real wind is evidence: a live reading held forward from minutes
  // ago (DELAYED, see liveMarkWind.applyGradeHold) must not be what condemns marks that are reading a real
  // dying breeze right now. A held reading is still eligible to be ignored itself — only its vote is dropped.
  const anyRealWind = marks.some(
    (m, i) =>
      Number.isFinite(speeds[i]) &&
      speeds[i] >= MARK_WIND_DEAD_TWS_KPH &&
      String(m?.DELAYED ?? "") !== "true"
  );
  if (!anyRealWind) return timestep;

  let changed = false;
  const nextMarks = marks.map((mark, i) => {
    const tws = speeds[i];
    if (!Number.isFinite(tws) || tws >= MARK_WIND_DEAD_TWS_KPH) return mark;
    changed = true;
    const next: Record<string, any> = { ...mark, WIND_IGNORED: true };
    for (const key of TWS_KEYS) delete next[key];
    for (const key of TWD_KEYS) delete next[key];
    return next;
  });

  return changed ? ({ ...timestep, MARKS: nextMarks } as T) : timestep;
}

/** `ignoreDeadMarkWind` over a whole series. Each timestep is judged on its own marks, never across time. */
export function ignoreDeadMarkWindSeries<T extends MarkWindTimestepLike>(
  timesteps: T[] | null | undefined
): T[] {
  if (!Array.isArray(timesteps) || timesteps.length === 0) return [];
  const out: T[] = [];
  for (const ts of timesteps) {
    const next = ignoreDeadMarkWind(ts);
    if (next) out.push(next);
  }
  return out;
}
